import { useEffect, useRef } from "react";
import { MessageCircle, HelpCircle } from "lucide-react";
import { waLink, WA_MESSAGES } from "@/lib/site";

const CSS_HREF = "https://static.onertravel.com/widget/search/production/styles.css";
const SCRIPT_SRC = "https://static.onertravel.com/widget/search/production/widget-befly.js";
const JQUERY_SRC = "https://code.jquery.com/jquery-3.7.1.min.js";

const ensureScript = (src) =>
  new Promise((resolve) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") return resolve();
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => resolve());
      return;
    }
    const el = document.createElement("script");
    el.src = src;
    el.async = false;
    el.addEventListener("load", () => {
      el.dataset.loaded = "true";
      resolve();
    });
    el.addEventListener("error", () => resolve());
    document.body.appendChild(el);
  });

// The external bundle defines a custom element exactly once per page lifetime
// and depends on a global jQuery. We load jQuery first, then the widget bundle,
// exactly once; on later mounts we just re-render the element markup.
let widgetScriptRequested = false;

// Renders the integrated travel search inside a premium branded card.
export const TravelSearchWidget = () => {
  const hostRef = useRef(null);

  useEffect(() => {
    if (!document.querySelector(`link[href="${CSS_HREF}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = CSS_HREF;
      document.head.appendChild(link);
    }

    if (hostRef.current && !hostRef.current.querySelector("befly-widget")) {
      hostRef.current.innerHTML =
        '<div id="wrapper"><befly-widget language="pt-br" new-tab="true"></befly-widget></div>';
    }

    if (!widgetScriptRequested) {
      widgetScriptRequested = true;
      (async () => {
        if (typeof window.jQuery === "undefined") {
          await ensureScript(JQUERY_SRC);
          window.$ = window.$ || window.jQuery;
        }
        await ensureScript(SCRIPT_SRC);
      })();
    }
  }, []);

  return (
    <div
      data-testid="travel-search-card"
      className="rounded-2xl border border-white/50 bg-white/90 p-2 shadow-premium backdrop-blur-xl sm:rounded-3xl sm:p-8"
    >
      <div
        ref={hostRef}
        data-testid="travel-search-widget"
        className="befly-widget-host min-h-[100px]"
      />

      <div className="mt-6 flex flex-col gap-3 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-center">
        <a
          href={waLink(WA_MESSAGES.consultant)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="search-talk-consultant"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white transition-all hover:scale-[1.02] hover:bg-[#1ebe5d]"
        >
          <MessageCircle size={18} /> Falar com uma consultora
        </a>
        <a
          href={waLink(WA_MESSAGES.doubts)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="search-have-doubts"
          className="inline-flex items-center justify-center gap-2 rounded-full border border-brand/30 bg-white px-6 py-3 text-sm font-semibold text-brand transition-all hover:bg-brand-light"
        >
          <HelpCircle size={18} /> Tenho dúvidas antes de viajar
        </a>
      </div>
    </div>
  );
};
